"use client";

import {
  Accordion,
  AccordionItem,
  type ListboxProps,
  type ListboxSectionProps,
  type Selection,
} from "@heroui/react";
import React from "react";
import { Listbox, Tooltip, ListboxItem, ListboxSection, Image } from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { useSidebar } from "./SidebarContext";

export enum SidebarItemType {
  Nest = "nest",
}

export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  img?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
};

export type SidebarProps = Omit<ListboxProps<SidebarItem>, "children"> & {
  items: SidebarItem[];
  isCompact?: boolean;
  hideEndContent?: boolean;
  iconClassName?: string;
  sectionClasses?: ListboxSectionProps["classNames"];
  classNames?: ListboxProps["classNames"];
  itemClasses?: ListboxProps<SidebarItem>["itemClasses"] & {
    brandTitle?: string;
  };
  defaultSelectedKey: string;
  onSelect?: (key: string) => void;
  handleNewChat?: () => void;
  itemClicked?: (item: SidebarItem) => void;
  showMenuTitle?: boolean;
};

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      isCompact,
      defaultSelectedKey,
      onSelect,
      hideEndContent,
      sectionClasses: sectionClassesProp = {},
      itemClasses: itemClassesProp = {},
      iconClassName,
      classNames,
      className,
      itemClicked,
      showMenuTitle,
      ...props
    },
    ref,
  ) => {
    const [selected, setSelected] = React.useState<React.Key>(defaultSelectedKey);
    const { isAuthenticated } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { brand } = useBrand();
    const { setShowChatHistory } = useSidebar();

    // Don't render anything if user is not authenticated
    if (!isAuthenticated) {
      return null;
    }

    const sectionClasses = {
      ...sectionClassesProp,
      base: cn(sectionClassesProp?.base, "w-full", {
        "p-0 max-w-[44px]": isCompact,
      }),
      group: cn(sectionClassesProp?.group, {
        "flex flex-col gap-1": isCompact,
      }),
      heading: cn(sectionClassesProp?.heading, {
        hidden: isCompact,
      }),
    };

    const itemClasses = {
      ...itemClassesProp,
      base: cn(itemClassesProp?.base, {
        "w-11 h-11 gap-0 p-0": isCompact,
      }),
    };

    const renderItem = (item: SidebarItem) => {
      const isNestType =
        item.items && item.items?.length > 0 && item?.type === SidebarItemType.Nest;

      if (isNestType) {
        return renderNestItem(item);
      }

      return (
        <ListboxItem
          {...item}
          key={item.key}
          aria-label={isCompact ? t(item.title) : "unknown"}
          data-id={`menu-${item.key}`}
          endContent={isCompact || hideEndContent ? null : (item.endContent ?? null)}
          href={undefined}
          startContent={
            isCompact ? null : item.icon ? (
              brand.menuItemsIcon === "image" && item.img ? (
                <Image
                  classNames={
                    brand.name === "Invest Ai" ? { img: "invest-ai-icon-filter" } : undefined
                  }
                  radius="none"
                  src={item.img}
                  width={20}
                />
              ) : (
                <Icon
                  className={cn(
                    "text-default-500 group-data-[selected=true]:text-foreground",
                    iconClassName,
                  )}
                  icon={item.icon}
                  width={20}
                />
              )
            ) : (
              (item.startContent ?? null)
            )
          }
          textValue={t(item.title)}
          title={isCompact ? null : t(item.title)}
          onPress={() => {
            if (item.href === "/chat") setShowChatHistory(true);
            else itemClicked?.(item);
            item.href && navigate(item.href);
          }}
        >
          {isCompact ? (
            <Tooltip content={t(item.title)} placement="right">
              <div className="flex w-full items-center justify-center">
                {item.icon ? (
                  brand.menuItemsIcon === "image" && item.img ? (
                    <Image
                      classNames={
                        brand.name === "Invest Ai" ? { img: "invest-ai-icon-filter" } : undefined
                      }
                      radius="none"
                      src={item.img}
                      width={20}
                    />
                  ) : (
                    <Icon
                      className={cn(
                        "text-default-500 group-data-[selected=true]:text-foreground",
                        iconClassName,
                      )}
                      icon={item.icon}
                      width={20}
                    />
                  )
                ) : (
                  (item.startContent ?? null)
                )}
              </div>
            </Tooltip>
          ) : null}
        </ListboxItem>
      );
    };

    const renderNestItem = (item: SidebarItem) => {
      const isNestType =
        item.items && item.items?.length > 0 && item?.type === SidebarItemType.Nest;

      if (isNestType) {
        // Is a nest type item , so we need to remove the href
        delete item.href;
      }

      return (
        <ListboxItem
          {...item}
          key={item.key}
          aria-label={isCompact || isNestType ? t(item.title) : "unknown"}
          classNames={{
            base: cn(
              {
                "h-auto p-0": !isCompact && isNestType,
              },
              {
                "inline-block w-11": isCompact && isNestType,
              },
            ),
          }}
          endContent={isCompact || isNestType || hideEndContent ? null : (item.endContent ?? null)}
          startContent={
            isCompact || isNestType ? null : item.icon ? (
              brand.menuItemsIcon === "image" && item.img ? (
                <Image
                  classNames={
                    brand.name === "Invest Ai" ? { img: "invest-ai-icon-filter" } : undefined
                  }
                  height={20}
                  radius="none"
                  src={item.img}
                />
              ) : (
                <Icon
                  className={cn(
                    "text-default-500 group-data-[selected=true]:text-foreground",
                    iconClassName,
                  )}
                  icon={item.icon}
                  width={20}
                />
              )
            ) : (
              (item.startContent ?? null)
            )
          }
          textValue={t(item.title)}
          title={isCompact || isNestType ? null : t(item.title)}
        >
          {isCompact ? (
            <Tooltip content={t(item.title)} placement="right">
              <div className="flex w-full items-center justify-center">
                {item.icon ? (
                  <Icon
                    className={cn(
                      "text-default-500 group-data-[selected=true]:text-foreground",
                      iconClassName,
                    )}
                    icon={item.icon}
                    width={20}
                  />
                ) : (
                  (item.startContent ?? null)
                )}
              </div>
            </Tooltip>
          ) : null}
          {!isCompact && isNestType ? (
            <Accordion className={"p-0"}>
              <AccordionItem
                key={item.key}
                aria-label={t(item.title)}
                classNames={{
                  heading: "pr-3",
                  trigger: "p-0",
                  content: "py-0 pl-4",
                }}
                title={
                  item.icon ? (
                    <div className={"flex h-11 items-center gap-2 px-2 py-1.5"}>
                      <Icon
                        className={cn(
                          "text-default-500 group-data-[selected=true]:text-foreground",
                          iconClassName,
                        )}
                        icon={item.icon}
                        width={20}
                      />
                      <span className="text-small font-medium text-default-500 group-data-[selected=true]:text-foreground">
                        {t(item.title)}
                      </span>
                    </div>
                  ) : (
                    (item.startContent ?? null)
                  )
                }
              >
                {item.items && item.items?.length > 0 ? (
                  <Listbox
                    aria-label={`${t(item.title)} items`}
                    className={"mt-0.5"}
                    classNames={{
                      list: cn("border-l border-default-200 pl-4"),
                    }}
                    items={item.items}
                    variant="flat"
                  >
                    {item.items.map(renderItem)}
                  </Listbox>
                ) : (
                  renderItem(item)
                )}
              </AccordionItem>
            </Accordion>
          ) : null}
        </ListboxItem>
      );
    };

    return (
      <Listbox
        key={isCompact ? "compact" : "default"}
        ref={ref}
        hideSelectedIcon
        aria-label="Navigation sidebar"
        as="nav"
        className={cn("list-none", className)}
        classNames={{
          ...classNames,
          list: cn("items-center", classNames?.list),
        }}
        color="default"
        itemClasses={{
          ...itemClasses,
          base: cn("px-3 data-[selected=true]:bg-default-100", itemClasses?.base),
          title: cn(
            "text-small font-medium text-default-500 group-data-[selected=true]:text-foreground",
            itemClasses?.title,
            itemClassesProp?.brandTitle,
          ),
        }}
        items={items}
        selectedKeys={[selected] as unknown as Selection}
        selectionMode="single"
        variant="flat"
        onSelectionChange={(keys) => {
          const key = Array.from(keys)[0];

          setSelected(key as React.Key);
          onSelect?.(key as string);
        }}
        {...props}
      >
        {(item) => {
          return item.items && item.items?.length > 0 && item?.type === SidebarItemType.Nest ? (
            renderNestItem(item)
          ) : item.items && item.items?.length > 0 ? (
            <ListboxSection
              key={item.key}
              aria-label={`${t(item.title)} section`}
              classNames={sectionClasses}
              showDivider={isCompact}
              title={showMenuTitle ? t(item.title) : ""}
            >
              {item.items.map(renderItem)}
            </ListboxSection>
          ) : (
            renderItem(item)
          );
        }}
      </Listbox>
    );
  },
);

Sidebar.displayName = "Sidebar";

export default Sidebar;
